import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useSearchParams } from "react-router-dom";
import { EmailPageLayout } from "@/components/email/EmailPageLayout";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/use-toast";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { CheckCircle2, Info, AlertTriangle, Loader2 } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { SUPABASE_URL } from "@/constants/supabase";

const formSchema = z.object({
  email: z.string().email({
    message: "Please enter a valid email address.",
  }),
  documentRequests: z.boolean().default(true),
  informationRequests: z.boolean().default(true),
  serviceUpdates: z.boolean().default(true),
  marketingCommunications: z.boolean().default(false),
  unsubscribeAll: z.boolean().default(false),
});

export default function EmailPreferences() {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tokenVerified, setTokenVerified] = useState(false);
  const [subscriberId, setSubscriberId] = useState<string | null>(null);

  // Get email and token from URL parameters
  const searchParams = useSearchParams()[0];
  const emailParam = searchParams.get("email") || "";
  const tokenParam = searchParams.get("token") || "";

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: emailParam,
      documentRequests: true,
      informationRequests: true,
      serviceUpdates: true,
      marketingCommunications: false,
      unsubscribeAll: false,
    },
  });

  // Verify token and load preferences when component mounts
  useEffect(() => {
    if (emailParam && tokenParam) {
      verifyTokenAndLoadPreferences(emailParam, tokenParam);
    }
  }, [emailParam, tokenParam]);

  // Function to verify token and load preferences
  async function verifyTokenAndLoadPreferences(email: string, token: string) {
    setIsLoading(true);
    setError(null);

    try {
      // Verify the token
      const verifyResponse = await fetch(
        `${SUPABASE_URL}/functions/v1/email-preferences/verify-token`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${supabase.auth.getSession()}`,
          },
          body: JSON.stringify({ email, token }),
        }
      );

      const verifyData = await verifyResponse.json();

      if (!verifyResponse.ok) {
        throw new Error(verifyData.error || "Failed to verify token");
      }

      // Token is valid, set verified state
      setTokenVerified(true);
      setSubscriberId(verifyData.subscriber_id);

      // Load preferences
      const preferencesResponse = await fetch(
        `${SUPABASE_URL}/functions/v1/email-preferences/get-preferences`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${supabase.auth.getSession()}`,
          },
          body: JSON.stringify({ email }),
        }
      );

      const preferencesData = await preferencesResponse.json();

      if (!preferencesResponse.ok) {
        throw new Error(preferencesData.error || "Failed to load preferences");
      }

      // Update form with preferences
      form.setValue("email", email);
      form.setValue(
        "documentRequests",
        preferencesData.preferences.documentRequests
      );
      form.setValue(
        "informationRequests",
        preferencesData.preferences.informationRequests
      );
      form.setValue(
        "serviceUpdates",
        preferencesData.preferences.serviceUpdates
      );
      form.setValue(
        "marketingCommunications",
        preferencesData.preferences.marketingCommunications
      );
      form.setValue(
        "unsubscribeAll",
        preferencesData.preferences.unsubscribeAll
      );

      toast({
        title: "Email Verified",
        description: "We've loaded your current email preferences.",
      });
    } catch (err) {
      console.error("Error verifying token or loading preferences:", err);
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
    } finally {
      setIsLoading(false);
    }
  }

  // Function to handle form submission
  async function onSubmit(values: z.infer<typeof formSchema>) {
    setIsLoading(true);
    setError(null);

    try {
      // Prepare the request body
      const requestBody = {
        email: values.email,
        token: tokenParam, // Include token if available
        preferences: {
          documentRequests: values.documentRequests,
          informationRequests: values.informationRequests,
          serviceUpdates: values.serviceUpdates,
          marketingCommunications: values.marketingCommunications,
          unsubscribeAll: values.unsubscribeAll,
        },
      };

      // Update preferences
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/email-preferences/update-preferences`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${supabase.auth.getSession()}`,
          },
          body: JSON.stringify(requestBody),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to update preferences");
      }

      setIsSubmitted(true);
      toast({
        title: "Preferences Updated",
        description: "Your email preferences have been updated successfully.",
      });
    } catch (err) {
      console.error("Error updating preferences:", err);
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
      toast({
        title: "Error",
        description:
          err instanceof Error ? err.message : "Failed to update preferences",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }

  // Function to verify email manually (when not using a token)
  async function handleVerifyEmail() {
    const email = form.getValues("email");
    if (!email) return;

    setIsVerifying(true);
    setError(null);

    try {
      // Get preferences for the email
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/email-preferences/get-preferences`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${supabase.auth.getSession()}`,
          },
          body: JSON.stringify({ email }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to load preferences");
      }

      // Update form with preferences
      form.setValue("documentRequests", data.preferences.documentRequests);
      form.setValue(
        "informationRequests",
        data.preferences.informationRequests
      );
      form.setValue("serviceUpdates", data.preferences.serviceUpdates);
      form.setValue(
        "marketingCommunications",
        data.preferences.marketingCommunications
      );
      form.setValue("unsubscribeAll", data.preferences.unsubscribeAll);

      toast({
        title: "Email Verified",
        description: "We've loaded your current email preferences.",
      });
    } catch (err) {
      console.error("Error loading preferences:", err);
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
      toast({
        title: "Error",
        description:
          err instanceof Error ? err.message : "Failed to load preferences",
        variant: "destructive",
      });
    } finally {
      setIsVerifying(false);
    }
  }

  return (
    <EmailPageLayout
      title="Email Preferences Center"
      description="Manage your communication preferences with Numberz Insight"
    >
      {isLoading && !error && !isSubmitted ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-10">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
            <p className="text-center text-muted-foreground">
              {tokenParam
                ? "Verifying your email and loading preferences..."
                : "Loading..."}
            </p>
          </CardContent>
        </Card>
      ) : error ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Error
            </CardTitle>
            <CardDescription>
              We encountered a problem processing your request.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">{error}</p>
            <p className="text-sm text-muted-foreground mb-4">
              This may be because your link has expired or is invalid. You can
              try entering your email address below to manage your preferences.
            </p>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-6"
              >
                <div className="flex flex-col gap-4 md:flex-row">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormLabel>Email Address</FormLabel>
                        <FormControl>
                          <div className="flex gap-2">
                            <Input
                              placeholder="your.email@example.com"
                              {...field}
                            />
                            <Button
                              type="button"
                              variant="outline"
                              onClick={handleVerifyEmail}
                              disabled={!field.value || isVerifying}
                            >
                              {isVerifying ? "Verifying..." : "Verify"}
                            </Button>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      ) : isSubmitted ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              Preferences Updated
            </CardTitle>
            <CardDescription>
              Your email preferences have been updated successfully.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              You will now receive emails based on your selected preferences.
              You can update these preferences at any time.
            </p>
            <Button onClick={() => setIsSubmitted(false)}>
              Update Preferences Again
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <Alert className="text-white dark:text-white">
              <Info className="h-4 w-4" />
              <AlertTitle className="text-white dark:text-white">
                Manage Your Email Preferences
              </AlertTitle>
              <AlertDescription className="text-white dark:text-white">
                Select which types of emails you'd like to receive from Numberz
                Insight. You can change these preferences at any time.
              </AlertDescription>
            </Alert>

            {!tokenVerified && (
              <div className="flex flex-col gap-4 md:flex-row">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormLabel className="text-white dark:text-white">
                        Email Address
                      </FormLabel>
                      <FormControl>
                        <div className="flex gap-2">
                          <Input
                            placeholder="your.email@example.com"
                            {...field}
                            disabled={!!tokenParam}
                            className="text-black dark:text-white"
                          />
                          {!tokenParam && (
                            <Button
                              type="button"
                              variant="outline"
                              onClick={handleVerifyEmail}
                              disabled={!field.value || isVerifying}
                            >
                              {isVerifying ? "Verifying..." : "Verify"}
                            </Button>
                          )}
                        </div>
                      </FormControl>
                      <FormDescription className="text-gray-200 dark:text-gray-300">
                        {tokenParam
                          ? "Your email has been verified through the link you clicked."
                          : "Enter the email address associated with your account."}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-white dark:text-white">
                Email Types
              </h3>

              <FormField
                control={form.control}
                name="documentRequests"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={form.watch("unsubscribeAll")}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel className="text-white dark:text-white">
                        Document Requests
                      </FormLabel>
                      <FormDescription className="text-gray-200 dark:text-gray-300">
                        Receive emails when your tax agent requests documents
                        from you.
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="informationRequests"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={form.watch("unsubscribeAll")}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel className="text-white dark:text-white">
                        Information Requests
                      </FormLabel>
                      <FormDescription className="text-gray-200 dark:text-gray-300">
                        Receive emails when your tax agent needs additional
                        information.
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="serviceUpdates"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={form.watch("unsubscribeAll")}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel className="text-white dark:text-white">
                        Service Updates
                      </FormLabel>
                      <FormDescription className="text-gray-200 dark:text-gray-300">
                        Receive emails about the status of your tax return and
                        other services.
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="marketingCommunications"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={form.watch("unsubscribeAll")}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel className="text-white dark:text-white">
                        Marketing Communications
                      </FormLabel>
                      <FormDescription className="text-gray-200 dark:text-gray-300">
                        Receive emails about new services, features, and
                        promotions.
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="unsubscribeAll"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4 bg-muted/50">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={(checked) => {
                          field.onChange(checked);
                          if (checked) {
                            form.setValue("documentRequests", false);
                            form.setValue("informationRequests", false);
                            form.setValue("serviceUpdates", false);
                            form.setValue("marketingCommunications", false);
                          }
                        }}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel className="text-white dark:text-white">
                        Unsubscribe from all emails
                      </FormLabel>
                      <FormDescription className="text-gray-200 dark:text-gray-300">
                        You will no longer receive any emails from Numberz
                        Insight except for essential account notifications.
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => (window.location.href = "/")}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save Preferences"
                )}
              </Button>
            </div>
          </form>
        </Form>
      )}
    </EmailPageLayout>
  );
}
